/**
 * Action catalogue — mirrors tile-auth/src/scopes.ts. Each entry is the
 * canonical 2-segment `<service>:<verb>` form; the UI translates to the
 * 8-segment ARN at save time using the action's `dimension`.
 *
 * `dimension` declares what resource axes the action scopes on:
 *   - 'org'      → action takes effect org-wide; no per-app/env picker.
 *   - 'app'      → action targets one or more apps within the org.
 *   - 'app+env'  → action targets specific (app, env) pairs.
 *
 * Mapping rule (apply at save):
 *   - dimension='org'      → ARN segments: orgId, *, *, *      (appId, envId both wildcards)
 *   - dimension='app'      → ARN segments: orgId, appId, service, *,  verb  (resourceId=*)
 *   - dimension='app+env'  → ARN segments: orgId, appId, service, envId, verb
 *
 * If the admin picks "all current + future" on an axis, that segment
 * becomes `*` so the policy keeps applying to new resources.
 */

export type ActionDimension = 'org' | 'app' | 'app+env';

export interface ActionDef {
  /** 2-segment scope id, e.g. 'releases:publishProd'. Matches SCOPES on the auth side. */
  id: string;
  /** Display name in the editor. */
  label: string;
  /** Optional one-liner shown next to the row. */
  help?: string;
  /** Resource axes this action scopes on. */
  dimension: ActionDimension;
}

export interface ActionGroup {
  label: string;
  actions: ActionDef[];
}

export const ACTION_GROUPS: ActionGroup[] = [
  {
    label: 'Org & members',
    actions: [
      { id: 'orgs:read', label: 'Read org', dimension: 'org' },
      { id: 'orgs:manage', label: 'Manage org (rename, delete)', dimension: 'org' },
      { id: 'users:read', label: 'List members', dimension: 'org' },
      { id: 'users:invite', label: 'Invite members', dimension: 'org' },
      { id: 'users:remove', label: 'Remove members', dimension: 'org' },
      { id: 'roles:assignViewer', label: 'Assign viewer role', dimension: 'org' },
      { id: 'roles:assignDeveloper', label: 'Assign developer role', dimension: 'org' },
      { id: 'roles:assignAdmin', label: 'Assign admin role', dimension: 'org' },
      { id: 'roles:assignOwner', label: 'Assign owner role (transfer)', dimension: 'org' },
    ],
  },
  {
    label: 'Apps',
    actions: [
      { id: 'apps:read', label: 'View apps', dimension: 'app' },
      { id: 'apps:create', label: 'Create apps', dimension: 'org' }, // create is org-wide; no target app yet
      { id: 'apps:write', label: 'Edit apps', dimension: 'app' },
      { id: 'apps:delete', label: 'Delete apps', dimension: 'app' },
    ],
  },
  {
    label: 'Saves & assets',
    actions: [
      { id: 'saves:read', label: 'Read saves', dimension: 'app' },
      { id: 'saves:write', label: 'Write saves', dimension: 'app' },
      { id: 'assets:read', label: 'View assets', dimension: 'app' },
      { id: 'assets:upload', label: 'Upload assets', dimension: 'app' },
    ],
  },
  {
    label: 'Branches & GitHub',
    actions: [
      { id: 'branches:read', label: 'View branches', dimension: 'app' },
      { id: 'branches:write', label: 'Create + switch branches', dimension: 'app' },
      { id: 'github:install', label: 'Connect GitHub', dimension: 'org' },
      { id: 'github:push', label: 'Push to GitHub', dimension: 'app' },
    ],
  },
  {
    label: 'Releases',
    actions: [
      { id: 'releases:read', label: 'View releases', dimension: 'app' },
      { id: 'releases:create', label: 'Create releases', dimension: 'app' },
      { id: 'releases:publishDev', label: 'Publish to dev', dimension: 'app+env' },
      { id: 'releases:publishProd', label: 'Publish to prod', dimension: 'app+env' },
      { id: 'releases:approveProd', label: 'Approve prod', dimension: 'app+env' },
      { id: 'releases:rollback', label: 'Roll back', dimension: 'app+env' },
    ],
  },
  {
    label: 'Environments',
    actions: [
      { id: 'envs:read', label: 'View environments', dimension: 'app' },
      { id: 'envs:manage', label: 'Manage environments', dimension: 'app+env' },
    ],
  },
  {
    label: 'Live Layer',
    actions: [
      { id: 'live_layer:read', label: 'View Live Layer', dimension: 'app+env' },
      { id: 'live_layer:publish', label: 'Publish (non-prod)', dimension: 'app+env' },
      { id: 'live_layer:publishProd', label: 'Publish to prod', dimension: 'app+env' },
      { id: 'live_layer:rollback', label: 'Roll back', dimension: 'app+env' },
      { id: 'live_layer:manage', label: 'Manage layers', dimension: 'app+env' },
    ],
  },
];

export const ALL_ACTIONS: ActionDef[] = ACTION_GROUPS.flatMap((g) => g.actions);
export const ACTION_BY_ID = new Map<string, ActionDef>(ALL_ACTIONS.map((a) => [a.id, a]));
export const ALL_ACTION_IDS: string[] = ALL_ACTIONS.map((a) => a.id);
